import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Navbar from '../../../../ReusableBlocks/Navbar';
import { PARTNER_PORTAL_NOT_AUTHORIZED, PARTNER_PORTAL_PAE_QUEUE } from '../Constants/partnerRoutes';
import {
  applyLeadershipQueueFilters,
  buildColumnFiltersFromLeadershipParams,
  buildLeadershipFilterLabel,
  getLeadershipKpiInitialStatus,
  parseLeadershipPaeQueueParams,
} from '../Utils/partnerLeadershipQueueUtils';
import { usePSMProjectQueue } from '../Hooks/usePSMProjectQueue';
import { usePartnerRoleAccess } from '../Hooks/usePartnerRoleAccess';
import { usePartnerTableSortFilter } from '../Hooks/usePartnerTableSortFilter';
import PartnerLayout from '../Layout/PartnerLayout';
import PageHeader from '../Components/shared/PageHeader';
import PartnerPagination from '../Components/shared/PartnerPagination';
import PartnerTableSortFilterHeader from '../Components/shared/PartnerTableSortFilterHeader';
import PartnerTableFiltersModal, {
  PartnerTableFiltersButton,
  PartnerTableResetFiltersButton,
} from '../Components/shared/PartnerTableFiltersModal';
import PartnerTableExportButton from '../Components/shared/PartnerTableExportButton';
import { exportPaeQueueCsv } from '../Utils/partnerProjectExportUtils';
import { PSMProjectDetailContent } from './PSMProjectDetailsPage';
import { PARTNER_TABLE_PAGE_SIZE, usePartnerPagination } from '../Hooks/usePartnerPagination';
import { formatPartnerTableDate } from '../Utils/partnerDateUtils';
import {
  countPaeQueueStatCards,
  matchesPaeQueueStatCardFilter,
  PAE_QUEUE_STAT_CARD_FILTERS,
} from '../Utils/partnerPsmQueueUtils';
import { getPaeQueueDisplayStatus } from '../Utils/partnerProjectExportUtils';
import { getPaeQueueTableStatusMeta } from '../Utils/partnerProjectStatusUtils';
import { resolveAssignedPaeFilterValue } from '../Utils/partnerUserUtils';
import PartnerTablePrimaryCell from '../Components/tables/PartnerTablePrimaryCell';
import PartnerTableProductsCell from '../Components/tables/PartnerTableProductsCell';
import PartnerTableStatusCell from '../Components/tables/PartnerTableStatusCell';
import PartnerTableActionsCell from '../Components/tables/PartnerTableActionsCell';
import {
  applyPartnerTableColumnFilters,
  buildPartnerTableRegionFilterOptions,
  buildPartnerTableStatusFilterOptions,
  getPaeQueueColumnValue,
  getPaeQueueSortValue,
  PAE_QUEUE_FILTER_COLUMNS,
  PAE_QUEUE_TABLE_COLUMNS,
} from '../Utils/partnerTableSortFilterUtils';

const getQueueStatus = getPaeQueueDisplayStatus;

const resolveProjectId = (project) => (
  project?.id
  ?? project?.projectId
  ?? project?.project_id
  ?? project?._id
  ?? null
);

const PSMQueuePage = () => {
  const access = usePartnerRoleAccess();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.auth?.currentUser);
  const { projects, partnerAdoptionEngineers, loading, error, refreshQueue, updateQueueProject } = usePSMProjectQueue();
  const [query, setQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  const leadershipParams = useMemo(
    () => parseLeadershipPaeQueueParams(searchParams),
    [searchParams],
  );

  const defaultPaeFilter = useMemo(
    () => resolveAssignedPaeFilterValue(currentUser, partnerAdoptionEngineers),
    [currentUser, partnerAdoptionEngineers],
  );

  const leadershipColumnFilters = useMemo(
    () => buildColumnFiltersFromLeadershipParams(leadershipParams),
    [leadershipParams],
  );

  // Default PAE Queue to the logged-in PAE's assignments. Choose "All" in Filters to see every project.
  // Leadership drill-downs stay unscoped by PAE so KPI/region/company views remain complete.
  const initialColumnFilters = useMemo(() => {
    if (leadershipParams) {
      return leadershipColumnFilters;
    }
    if (defaultPaeFilter) {
      return {
        ...leadershipColumnFilters,
        pae: defaultPaeFilter,
      };
    }
    return leadershipColumnFilters;
  }, [leadershipParams, leadershipColumnFilters, defaultPaeFilter]);

  const leadershipFilterLabel = useMemo(
    () => buildLeadershipFilterLabel(leadershipParams),
    [leadershipParams],
  );

  const leadershipScopedProjects = useMemo(
    () => applyLeadershipQueueFilters(projects, leadershipParams),
    [projects, leadershipParams],
  );

  useEffect(() => {
    if (!leadershipParams?.kpi) {
      return;
    }
    setSelectedStatus(getLeadershipKpiInitialStatus(leadershipParams.kpi));
  }, [leadershipParams]);

  useEffect(() => {
    const projectIdFromUrl = searchParams.get('project');
    setSelectedProjectId(projectIdFromUrl || null);
  }, [searchParams]);

  const statusFilterOptions = useMemo(
    () => buildPartnerTableStatusFilterOptions(projects, getQueueStatus),
    [projects],
  );

  const matchesQueueSearch = useCallback((project, queryValue) => {
    if (!queryValue) {
      return true;
    }
    const partner = (project.partnerName || '').toLowerCase();
    const customer = (project.customerName || '').toLowerCase();
    const lead = (project.projectLeadName || '').toLowerCase();
    const company = (project.companyName || '').toLowerCase();
    const partnerService = (project.partnerServiceName || '').toLowerCase();
    return partner.includes(queryValue) || customer.includes(queryValue)
      || lead.includes(queryValue) || company.includes(queryValue)
      || partnerService.includes(queryValue);
  }, []);

  const filteredProjects = useMemo(() => {
    const queryValue = query.trim().toLowerCase();

    return leadershipScopedProjects.filter((project) => {
      if (!matchesPaeQueueStatCardFilter(project, selectedStatus)) {
        return false;
      }
      return matchesQueueSearch(project, queryValue);
    });
  }, [leadershipScopedProjects, query, selectedStatus, matchesQueueSearch]);

  const getColumnValue = useCallback(
    (project, columnKey) => getPaeQueueColumnValue(project, columnKey),
    [],
  );
  const getSortValue = useCallback(
    (project, columnKey) => getPaeQueueSortValue(project, columnKey),
    [],
  );

  const regionFilterOptions = useMemo(
    () => buildPartnerTableRegionFilterOptions(projects, getColumnValue),
    [projects, getColumnValue],
  );

  const {
    columnFilters,
    applyColumnFilters,
    hasActiveColumnFilters,
    resetColumnFilters,
    sortKey,
    sortDirection,
    toggleSort,
    processedProjects,
    columnFiltersKey,
    sortKeyValue,
  } = usePartnerTableSortFilter(filteredProjects, {
    getColumnValue,
    getSortValue,
    initialSort: { key: 'partnerServiceName', direction: 'desc' },
    initialColumnFilters,
  });

  // Stat tiles follow search + column filters (e.g. partner), but ignore the status tile
  // so Active / At risk / etc. stay meaningful within the filtered set.
  const statsBaseProjects = useMemo(() => {
    const queryValue = query.trim().toLowerCase();
    const searchScoped = leadershipScopedProjects.filter((project) => (
      matchesQueueSearch(project, queryValue)
    ));
    return applyPartnerTableColumnFilters(searchScoped, columnFilters, getColumnValue);
  }, [leadershipScopedProjects, query, columnFilters, getColumnValue, matchesQueueSearch]);

  const queueCounts = useMemo(
    () => countPaeQueueStatCards(statsBaseProjects),
    [statsBaseProjects],
  );

  const {
    paginatedItems: paginatedProjects,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    setCurrentPage,
  } = usePartnerPagination(
    processedProjects,
    PARTNER_TABLE_PAGE_SIZE,
    `${query}:${selectedStatus}:${columnFiltersKey}:${sortKeyValue}`,
  );

  const handleClearLeadershipFilter = () => {
    navigate(PARTNER_PORTAL_PAE_QUEUE, { replace: true });
  };

  const hasActiveToolbarFilters = hasActiveColumnFilters
    || Boolean(query.trim())
    || selectedStatus !== 'All'
    || Boolean(leadershipParams);

  const handleResetAllFilters = () => {
    setQuery('');
    setSelectedStatus('All');
    if (leadershipParams) {
      handleClearLeadershipFilter();
      applyColumnFilters(defaultPaeFilter ? { pae: defaultPaeFilter } : {});
      return;
    }
    resetColumnFilters();
  };

  const showProjectDetails = (projectId) => {
    if (!projectId) {
      return;
    }
    setSelectedProjectId(projectId);
    const params = new URLSearchParams(searchParams);
    params.set('project', String(projectId));
    navigate(`${PARTNER_PORTAL_PAE_QUEUE}?${params.toString()}`, { replace: true });
  };

  const handleProjectUpdated = (updatedProject) => {
    const updatedProjectId = resolveProjectId(updatedProject);
    if (!updatedProjectId) {
      return;
    }
    updateQueueProject(updatedProjectId, { ...updatedProject, id: updatedProjectId });
  };

  const handleBackToQueue = async () => {
    setSelectedProjectId(null);
    const params = new URLSearchParams(searchParams);
    params.delete('project');
    const queryString = params.toString();
    navigate(
      queryString ? `${PARTNER_PORTAL_PAE_QUEUE}?${queryString}` : PARTNER_PORTAL_PAE_QUEUE,
      { replace: true },
    );
    await refreshQueue();
  };

  const handleExport = () => {
    exportPaeQueueCsv(processedProjects);
  };

  const handleQueueStatusFilter = (filterValue) => {
    if (!filterValue) {
      setSelectedStatus('All');
      setCurrentPage(1);
      return;
    }
    setSelectedStatus((previous) => (previous === filterValue ? 'All' : filterValue));
    setCurrentPage(1);
  };

  const totalPoolCount = leadershipScopedProjects.length;
  const showFilteredSubset = totalItems !== totalPoolCount;

  const paeStatCards = [
    {
      key: 'active',
      label: 'Active',
      hint: 'Delivery in progress',
      value: queueCounts.active,
      filterValue: PAE_QUEUE_STAT_CARD_FILTERS.ACTIVE,
      tone: 'active',
    },
    {
      key: 'at-risk',
      label: 'At risk',
      hint: 'Go-live at risk',
      value: queueCounts.atRisk,
      filterValue: PAE_QUEUE_STAT_CARD_FILTERS.AT_RISK,
      tone: 'at-risk',
    },
    {
      key: 'completed',
      label: 'Completed',
      hint: 'Closed deliveries',
      value: queueCounts.completed,
      filterValue: PAE_QUEUE_STAT_CARD_FILTERS.COMPLETED,
      tone: 'completed',
    },
    {
      key: 'needs-review',
      label: 'Needs review',
      hint: 'Pending PAE review',
      value: queueCounts.needsReview,
      filterValue: PAE_QUEUE_STAT_CARD_FILTERS.NEEDS_REVIEW,
      tone: 'needs-review',
    },
    {
      key: 'sales-cycle',
      label: 'Sales cycle',
      hint: 'Not started',
      summaryLabel: 'Sales cycle',
      value: queueCounts.salesCycle,
      filterValue: PAE_QUEUE_STAT_CARD_FILTERS.SALES_CYCLE,
      tone: 'sales',
    },
  ];

  const activeStatCard = paeStatCards.find(
    (card) => card.filterValue === selectedStatus,
  );
  const activeStatSummary = activeStatCard?.summaryLabel || activeStatCard?.label;

  if (!access.canPerformPAEOperations) {
    return <Navigate to={PARTNER_PORTAL_NOT_AUTHORIZED} replace />;
  }

  if (loading) {
    return (
      <>
        <Navbar activeTab="PARTNER_PORTAL" />
        <PartnerLayout access={access}>
          <p className="partner-loading-text">Loading project queue...</p>
        </PartnerLayout>
      </>
    );
  }

  return (
    <>
      <Navbar activeTab="PARTNER_PORTAL" />
      <PartnerLayout access={access}>
        {selectedProjectId ? (
          <PSMProjectDetailContent
            projectId={selectedProjectId}
            onBackToQueue={handleBackToQueue}
            onProjectUpdated={handleProjectUpdated}
          />
        ) : (
          <div className="partner-pae-queue-page">
            <PageHeader
              title="PAE Queue"
              description="New partner project submissions pending PAE review"
              rightContent={<span className="psm-queue-view-pill">PAE View</span>}
            />

            {error ? <p className="text-danger">{error}</p> : null}

            {leadershipFilterLabel ? (
              <div className="partner-pae-leadership-banner">
                <div>
                  <strong>Filtered from Leadership Dashboard</strong>
                  <p>{leadershipFilterLabel}</p>
                </div>
                <button
                  type="button"
                  className="partner-pae-leadership-banner-clear"
                  onClick={handleClearLeadershipFilter}
                >
                  Clear filter
                </button>
              </div>
            ) : null}

            <div className="partner-pae-stats-row">
              {paeStatCards.map((card) => {
                const isActive = selectedStatus === card.filterValue;
                return (
                  <button
                    key={card.key}
                    type="button"
                    className={`partner-portal-stat-card partner-portal-stat-card--${card.tone} ${isActive ? 'is-active' : ''}`}
                    aria-pressed={isActive}
                    onClick={() => handleQueueStatusFilter(card.filterValue)}
                  >
                    <span className="partner-portal-stat-card-accent" aria-hidden="true" />
                    <span className="partner-portal-stat-card-body">
                      <span className="partner-portal-stat-card-top">
                        <span className="partner-portal-stat-card-value">{card.value}</span>
                        <span className="partner-portal-stat-card-label">{card.label}</span>
                      </span>
                      <span className="partner-portal-stat-card-hint">{card.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            <section className="partner-my-workspace" aria-label="PAE project queue">
              <PartnerTableFiltersModal
                isOpen={isFiltersModalOpen}
                title="PAE Queue Filters"
                columns={PAE_QUEUE_FILTER_COLUMNS}
                projects={filteredProjects}
                appliedFilters={columnFilters}
                getColumnValue={getColumnValue}
                externalFilterOptions={{
                  pae: partnerAdoptionEngineers,
                  status: statusFilterOptions,
                  region: regionFilterOptions,
                }}
                onClose={() => setIsFiltersModalOpen(false)}
                onApply={applyColumnFilters}
              />

              <div className="partner-my-table-panel partner-pae-queue-panel">
                <div className="partner-my-table-panel-head">
                  <div className="partner-my-table-panel-head-summary">
                    <p className="partner-my-project-count" aria-live="polite">
                      <span className="partner-my-project-count-number">
                        {totalPoolCount.toLocaleString()}
                      </span>
                      {' '}
                      <span className="partner-my-project-count-label">
                        total
                        {' '}
                        {totalPoolCount === 1 ? 'project' : 'projects'}
                      </span>
                      {showFilteredSubset ? (
                        <>
                          <span className="partner-my-project-count-sep" aria-hidden="true">·</span>
                          <span className="partner-my-project-count-number partner-my-project-count-number--filtered">
                            {totalItems.toLocaleString()}
                          </span>
                          {selectedStatus !== 'All' && activeStatSummary ? (
                            <>
                              <span className="partner-my-project-count-sep" aria-hidden="true">·</span>
                              <span className="partner-my-project-count-page">{activeStatSummary}</span>
                            </>
                          ) : null}
                        </>
                      ) : null}
                      {totalPages > 1 ? (
                        <>
                          <span className="partner-my-project-count-sep" aria-hidden="true">·</span>
                          <span className="partner-my-project-count-page">
                            Page
                            {' '}
                            {currentPage}
                            {' '}
                            of
                            {' '}
                            {totalPages}
                          </span>
                        </>
                      ) : null}
                    </p>
                    {hasActiveToolbarFilters ? (
                      <span className="partner-my-filtered-badge">Filtered</span>
                    ) : null}
                  </div>
                  <div className="partner-my-table-panel-head-toolbar">
                    <label className="partner-my-search">
                      <span className="partner-my-search-icon" aria-hidden="true" />
                      <input
                        className="form-control partner-my-search-input"
                        placeholder="Search partner, customer, company..."
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                      />
                    </label>
                    <div className="partner-my-toolbar-actions partner-my-filter-action-group">
                      <PartnerTableFiltersButton
                        onClick={() => setIsFiltersModalOpen(true)}
                        hasActiveFilters={hasActiveColumnFilters}
                      />
                      <PartnerTableResetFiltersButton
                        onClick={handleResetAllFilters}
                        disabled={!hasActiveToolbarFilters}
                      />
                      <PartnerTableExportButton
                        onClick={handleExport}
                        itemCount={processedProjects.length}
                      />
                    </div>
                  </div>
                </div>

                <div className="psm-queue-table-container psm-queue-screen-table partner-pae-queue-table-wrap">
              <table className="table psm-queue-table partner-portal-list-table">
                <colgroup>
                  <col className="psm-queue-col-partner" />
                  <col className="psm-queue-col-company" />
                  <col className="psm-queue-col-partner-service" />
                  <col className="psm-queue-col-customer" />
                  <col className="psm-queue-col-products" />
                  <col className="psm-queue-col-engagement" />
                  <col className="psm-queue-col-status" />
                  <col className="psm-queue-col-est-start" />
                  <col className="psm-queue-col-est-golive" />
                  <col className="psm-queue-col-act-start" />
                  <col className="psm-queue-col-act-golive" />
                  <col className="psm-queue-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    {PAE_QUEUE_TABLE_COLUMNS.map((column) => (
                      <PartnerTableSortFilterHeader
                        key={column.key}
                        label={column.label}
                        columnKey={column.key}
                        sortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={toggleSort}
                        sortable={column.sortable}
                        className={
                          column.key === 'status'
                            ? 'psm-queue-status-head'
                            : ['estStart', 'estGolive', 'actStart', 'actGolive'].includes(column.key)
                              ? 'psm-queue-date-head'
                              : column.key === 'partnerServiceName'
                                ? 'psm-queue-partner-service-head'
                                : ''
                        }
                      />
                    ))}
                    <PartnerTableSortFilterHeader actions className="psm-queue-actions-head" />
                  </tr>
                </thead>
                <tbody>
                  {paginatedProjects.map((project) => {
                    const rowProjectId = resolveProjectId(project);
                    const statusMeta = getPaeQueueTableStatusMeta(project);
                    return (
                      <tr key={rowProjectId || `${project.partnerName || 'partner'}-${project.customerName || 'customer'}`}>
                        <td>
                          <PartnerTablePrimaryCell
                            title={project.partnerName}
                            subtitle={project.projectLeadName || project.projectLeadEmail}
                          />
                        </td>
                        <td className="partner-table-company-cell">{project.companyName || '-'}</td>
                        <td className="partner-table-company-cell">{project.partnerServiceName || '-'}</td>
                        <td className="partner-table-company-cell">{project.customerName || '-'}</td>
                        <td>
                          <PartnerTableProductsCell
                            products={project.productsInScope}
                            rowKeyPrefix={rowProjectId || 'project'}
                          />
                        </td>
                        <td className="partner-table-engagement-cell">{project.engagementType || '-'}</td>
                        <PartnerTableStatusCell meta={statusMeta} />
                        <td className="partner-table-date-cell">{formatPartnerTableDate(project.estimatedStartDate)}</td>
                        <td className="partner-table-date-cell">{formatPartnerTableDate(project.estimatedGoliveDate)}</td>
                        <td className="partner-table-date-cell">{formatPartnerTableDate(project.actualStartDate)}</td>
                        <td className="partner-table-date-cell">{formatPartnerTableDate(project.actualGoliveDate)}</td>
                        <PartnerTableActionsCell onView={() => showProjectDetails(rowProjectId)} />
                      </tr>
                    );
                  })}
                  {paginatedProjects.length === 0 ? (
                    <tr>
                      <td colSpan={PAE_QUEUE_TABLE_COLUMNS.length + 1} className="text-center">No projects found.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
                </div>
                <PartnerPagination
                  className="partner-pagination--my-panel"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  itemLabel="projects"
                  onPageChange={setCurrentPage}
                />
              </div>
            </section>
          </div>
        )}
      </PartnerLayout>
    </>
  );
};

export default PSMQueuePage;
